import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { CallbackType, Config, FRAuth, FRStep, StepType } from '@forgerock/javascript-sdk';
import { Action, RequestObj } from '@forgerock/javascript-sdk/lib/config/interfaces';
import { StepOptions } from '@forgerock/javascript-sdk/lib/auth/interfaces';
import { ConfigurationService, Locale, LocaleService } from '@ciam/library';
import { from, Observable } from 'rxjs';
import { APIResponse, Output } from '../mfa-pin/APIResponse';

/** Name of the NameCallback output that holds its prompt/label. */
const PROMPT_OUTPUT_KEY = 'prompt';

/** Prompt of the NameCallback that carries the one time passcode in the journey. */
const OTP_PROMPT = 'One Time Passcode';

/** Id of the HiddenValueCallback that carries the delivery-channel preferences (masked address). */
const DELIVERY_PREFERENCES_ID = 'deliveryChannelPreferences';

/**
 * Request parameter that tells the journey to skip the "email already verified" short-circuit and
 * route straight to the change-email + OTP path. Without it the journey completes immediately for a
 * user whose current email is already verified.
 */
const VERIFY_WITH_OTP_ONLY_QUERY: StepOptions = { query: { verifyWithOtpOnly: 'true' } };

/** FR error codes that represent an invalid/expired OTP (retryable) rather than a system failure. */
const OTP_INVALID_CODES = ['USER.PIN.INVALIDOREXPIRED', 'PIN.VELOCITY.FAILURE', 'OTP.LIMIT.REACHED'];

/** ConfirmationCallback index the journey expects for each user action on the OTP page. */
export enum OtpAction {
  SUBMIT = 0,
  SKIP = 1,
  RESEND = 2,
  CHANGE_METHOD = 3
}

/** ConfirmationCallback index the journey expects on the verify-options page. */
export enum OptionsAction {
  CONTINUE = 0,
  CHANGE_EMAIL = 1
}

/**
 * Drives the ForgeRock `user_email_verification` journey used when a user changes their
 * email address in the profile. Mirrors the WLGN post-login email-verification service so
 * both applications share the same journey contract.
 */
@Injectable({
  providedIn: 'root'
})
export class EmailVerificationService {
  currentStep: any;
  private locale: Locale | undefined;

  constructor(
    private configService: ConfigurationService,
    private localeService: LocaleService) {
    this.localeService.watch$().subscribe({
      next: (locale) => this.locale = locale
    });
  }

  setFRConfigs(): void {
    const addHeadersMiddleware = (
      req: RequestObj,
      action: Action,
      next: () => RequestObj
    ): void => {
      switch (action.type) {
        case 'START_AUTHENTICATE':
        case 'AUTHENTICATE':
          const headers = req.init.headers as Headers;
          headers.append('x-locale', this.localeService.solveLocale().posix);
          break;
      }
      next();
    };
    Config.set({
      clientId: this.configService.select<string>('env.frClientId'),
      middleware: [addHeadersMiddleware],
      scope: 'openid profile email address phone',
      serverConfig: {
        baseUrl: this.configService.select<string>('env.frUrl') || '',
        timeout: 5000
      },
      realmPath: this.getForgeRockRealm(),
      tree: 'user_email_verification'
    });
  }

  initFRConfig(): Observable<any | APIResponse | HttpErrorResponse> {
    this.setFRConfigs();
    return from(this.nextStep(undefined, VERIFY_WITH_OTP_ONLY_QUERY));
  }

  nextStep(currentStep: FRStep | undefined, options?: StepOptions): Promise<any | APIResponse | HttpErrorResponse> {
    return FRAuth.next(currentStep, options)
      .then((step) => {
        this.currentStep = step;
        return step;
      })
      .catch(() => Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', 'OTP Init')));
  }

  /**
   * Submit the entered OTP for the email-verification journey.
   *
   * @param pin - one time passcode entered by the user
   * @returns resolves with an APIResponse on success; errors with status 400 (invalid/expired) or 500 (system)
   */
  verifyOtp(pin: string): Observable<APIResponse | HttpErrorResponse> {
    this.setOtpAction(OtpAction.SUBMIT);
    this.setOtpValue(pin);
    return from(this.submitStep('OTP Submit'));
  }

  /**
   * Request a new OTP by choosing "Request New Code" on the journey's confirmation callback.
   *
   * @returns resolves when a fresh code has been sent; errors on system failure
   */
  resendOtp(): Observable<APIResponse | HttpErrorResponse> {
    this.setOtpAction(OtpAction.RESEND);
    return from(this.submitStep('OTP Resend'));
  }

  continueVerification(): Observable<APIResponse | HttpErrorResponse> {
    this.setOtpAction(OptionsAction.CONTINUE);
    return from(this.submitStep('Options Continue'));
  }

  changeEmail(): Observable<APIResponse | HttpErrorResponse> {
    this.setOtpAction(OptionsAction.CHANGE_EMAIL);
    return from(this.submitStep('Options Change Email'));
  }

  /**
   * Submit a new email address on the change-email step of the journey.
   *
   * @param email - the new email address entered by the user
   * @returns resolves with an APIResponse on success; errors with status 400 (validation) or 500 (system)
   */
  submitNewEmail(email: string): Observable<APIResponse | HttpErrorResponse> {
    this.setEmailValue(email);
    return from(this.submitStep('Change Email Submit'));
  }

  /**
   * Read the masked delivery address (e.g. "mf***@f***.com") from the current OTP step.
   *
   * @returns the masked address, or '' when it cannot be resolved
   */
  getVerificationAddress(): string {
    const hidden = this.currentStep?.callbacks?.find((callback) =>
      callback.getType() === CallbackType.HiddenValueCallback &&
      callback.getOutputValue('id') === DELIVERY_PREFERENCES_ID);
    if (!hidden) {
      return '';
    }
    try {
      const preferences: Output = JSON.parse(hidden.getOutputValue('value'));
      const channels = preferences.deliveryChannelPreferences;
      const channel = channels?.find((entry) => entry.deliveryMethod === preferences.preferredDeliveryMethod)
        ?? channels?.[0];
      const method = channel?.deliveryMethods?.find((entry) => entry.type === channel.preferredDeliveryType)
        ?? channel?.deliveryMethods?.[0];
      return method?.value ?? '';
    } catch {
      return '';
    }
  }

  private submitStep(action: string): Promise<APIResponse | HttpErrorResponse> {
    if (!this.currentStep) {
      return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action));
    }
    return FRAuth.next(this.currentStep)
      .catch(() => Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action)))
      .then((step) => this.handleStep(step, action));
  }

  /**
   * Map an FR journey step to a success response or a rejected error.
   * - LoginSuccess / intermediate Step: OTP accepted or code resent -> resolve without error.
   * - Step carrying payload.description: journey signalled an invalid/expired OTP -> reject 400.
   * - LoginFailure: OTP error codes -> reject 400, anything else -> reject 500 (system unavailable).
   *
   * @param step - the step returned by FRAuth.next
   * @param action - label used for logging/error context
   */
  handleStep(step: any, action?: string): Promise<APIResponse> {
    if (step?.payload?.description) {
      this.currentStep = step;
      return Promise.reject(this.buildError(HttpStatusCode.BadRequest, step.payload.description, action));
    }
    switch (step?.type as StepType) {
      case StepType.LoginSuccess:
      case StepType.Step:
        this.currentStep = step;
        return Promise.resolve({ output: {} });
      case StepType.LoginFailure:
        // Keep the previous callback-bearing step for any LoginFailure so the user can retry or
        // resend the OTP; the failure payload carries no callbacks and would break the next request.
        return Promise.reject(this.buildError(this.getFailureStatus(step), step?.payload?.message, action));
      default:
        this.currentStep = step;
        return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action));
    }
  }

  private getFailureStatus(step: any): number {
    return OTP_INVALID_CODES.includes(step?.payload?.message) ? HttpStatusCode.BadRequest : HttpStatusCode.InternalServerError;
  }

  private setOtpAction(value: OtpAction | OptionsAction): void {
    this.currentStep?.callbacks
      ?.filter((callback) => callback.getType() === CallbackType.ConfirmationCallback)
      .forEach((callback) => callback.setInputValue(value));
  }

  private setOtpValue(pin: string): void {
    this.currentStep?.callbacks
      ?.filter((callback) => callback.getType() === CallbackType.NameCallback && callback.getOutputValue(PROMPT_OUTPUT_KEY) === OTP_PROMPT)
      .forEach((callback) => callback.setInputValue(pin));
  }

  private setEmailValue(email: string): void {
    this.currentStep?.callbacks
      ?.find((callback) => callback.getType() === CallbackType.StringAttributeInputCallback)
      ?.setInputValue(email);
  }

  private buildError(status: number, code: string, action?: string): HttpErrorResponse {
    return {
      error: { errors: [{ code, message: '' }] },
      status,
      url: this.configService.select<string>('env.frUrl') || '',
      message: `Http failure response on '${action}': '${code}'`
    } as unknown as HttpErrorResponse;
  }

  getForgeRockRealm(): string {
    if (this.locale?.country === 'cn') {
      return this.configService.select<string>('forgeRock.realm.cdl') || 'fedexCDN';
    }
    return this.configService.select<string>('forgeRock.realm.default') || 'alpha';
  }
}
